import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { formatDate, getCSRFToken } from "../../lib/utils";
import { setUserAdminLocked } from "../../lib/adminUserLock";
import { PaginationMeta } from "../../types/PaginationMeta";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import { Button } from "../../components/BaseComponents";
import useDataSort from "../../hooks/useDataSort";
import usePagination from "../../hooks/usePagination";
import useCleanup from "../../hooks/useCleanup";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";

type AdminCompanyUsersProps = {
    companyId: string;
}

function AdminCompanyUsers({ companyId }: AdminCompanyUsersProps) {
    const [results, setResults] = useState<any[]>([])

    const [loading, setLoading] = useState(false);
    const dataSort = useDataSort('updated_at', 'desc');
    const pagination = usePagination();

    const [deletionCandidate, setDeletionCandidate] = useState<any>(null);

    const api = useApi();
    const onSearch = useCleanup(() => {
        let cancelled = false;
        
        const run = async () => {
            setLoading(true);
            const qs = new URLSearchParams({
                ...pagination.params,
                ...dataSort.params,
            })
            const apiResult = await api.get(`/admin/company/${companyId}/users?` + qs.toString());
            if(cancelled) {
                return;
            }
            setResults(apiResult.data.data);
            pagination.readMeta(apiResult.data.meta);
            setLoading(false);
        }
        run()
        return () => {
            console.log('cancel')
            cancelled = true;
        }
    });

    const onLockUser = async (userId: string, locked: boolean) => {
        setLoading(true);
        await setUserAdminLocked(api, userId, locked)
        await onSearch();
    }

    const onDeleteUser = async (userId: string) => {
        setLoading(true);
        setDeletionCandidate(null);
        await api.delete(`/admin/user/${userId}`, {
            _csrf: await getCSRFToken(),
        })
        await onSearch();
    }

    useEffect(() => {
        onSearch();
    }, [pagination.params, dataSort.params])
    
    const dateRenderer = (date: string) => {
        return <span title={date}>{ formatDate(date) }</span>
    }
    const columns: TableColumn[] = [{
        id: 'full_name',
        label: 'Full Name',
        sortable: true,
        renderer: (key, row) => <span>{ row[key] }{ row.row_type === 'invitation' ? <span className="text-charcoal-300"> (pending)</span> : '' }</span>,
    },{
        id: 'email',
        label: 'Email',
        sortable: true,
    }, {
        id: 'role.',
        label: 'Role',
        renderer: (key, row) => <span>{ row.role?.name ?? '-' }</span>
    }, {
        id: 'email_confirmed',
        label: 'Email Confirmed',
        renderer: (key, row) => <span>{ row[key] ? 'Yes' : 'No' }</span>,
        sortable: true,
    }, {
        id: 'created_at',
        label: 'Created',
        renderer: (key, row) => dateRenderer(row[key]),
        sortable: true,
    }, {
        id: 'updated_at', 
        label: 'Updated At',
        renderer: (key, row) => dateRenderer(row[key]),
        sortable: true,
    }, {
        id: 'admin_locked',
        label: 'Locked',
        renderer: (key, row) => {
            if (row.row_type === 'invitation') {
                return <span>-</span>;
            }
            const userId = row['id'];
            const isLocked = row['admin_locked'];
            return (
                <Button
                    type="Primary"
                    size="sm"
                    text={`${isLocked ? 'Unlock' : 'Lock'} User`}
                    onClick={() => onLockUser(userId, !isLocked)}
                />
            );
        },
        sortable: true,
    }, {
        id: 'delete',
        label: 'Delete',
        renderer: (key, row) => {
            const isInvitation = row.row_type === 'invitation';
            return (
                <Button
                    type="Primary"
                    size="sm"
                    text={isInvitation ? 'Revoke invite' : 'Delete user'}
                    onClick={() => setDeletionCandidate(row)}
                />
            );
        },
        sortable: true,
    }]
    return (
        <div>
            <h3 className="text-white mb-3 mt-8">Users</h3>
            <SimpleSortTable
                columns={columns}
                data={results}
                isLoading={loading}
				defaultSorting={dataSort.defaultSorting}
				onSortPress={dataSort.onChange}
            />
			<Pagination 
				onPageChange={pagination.onPageChange}
				pagination={pagination.pagination}
                isLoading={loading}
			/>
            <ConfirmationModal
                open={!!deletionCandidate}
                onCancel={() => setDeletionCandidate(null)}
                onConfirm={() => deletionCandidate && onDeleteUser(deletionCandidate.id)}
            >
                { deletionCandidate && (
                    <div>
                        <PageHeadings title={deletionCandidate.row_type === 'invitation' ? 'Revoke invite?' : 'Delete user?'}/>
                        <p className="text-white mt-4">User name:{ deletionCandidate.full_name}</p>
                        <p className="text-white">User email:{ deletionCandidate.email}</p>
                        <p className="text-white">
                            { deletionCandidate.row_type === 'invitation'
                                ? 'This will revoke the open invitation.'
                                : 'This will irreversibly anonymise the name and email and lock the user.' }
                        </p>
                    </div>
                )}
            </ConfirmationModal>
        </div>
    )
}

export default AdminCompanyUsers;
